import { sanitizeBrandOverride } from './chunk-P4WSDWOY.js';
import { ChainAvatar } from './chunk-UHTGLWOP.js';
import { Tabs, TabsList, TabsTrigger, TabsContent, NativeSelect } from './chunk-XGR2NXWI.js';
import { useCopyToClipboard, CopyButton } from './chunk-DIDPLGSY.js';
import { useOptionalTheme } from './chunk-77MV73QS.js';
import { Input } from './chunk-W2ZVUGEI.js';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './chunk-PEIGN6MO.js';
import { Badge } from './chunk-2WDEGOCV.js';
import { Button } from './chunk-QJZK7P2F.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React from 'react';
import { coerceTenantContext, accessMatrix, personLabel, peopleIn, toMermaid } from '@authe/fe-commons/tenancy';
import { EMPTY_TENANCY_NAMES } from '@authe/fe-commons/auth';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { settlementStage, SettlementEvidenceSchema } from '@authe/fe-commons/metadata-analytics';
import { formatUnits } from '@authe/fe-commons/value';
import { shortenHash } from '@authe/fe-commons/chains';

var TenantReactContext = React.createContext(null);
function TenantProvider({
  tenant,
  names,
  branding,
  brandingOptions,
  children
}) {
  const resolved = React.useMemo(() => coerceTenantContext(tenant), [tenant]);
  const theme = useOptionalTheme();
  const setBrand = theme?.setBrand;
  React.useEffect(() => {
    if (!setBrand) return;
    setBrand(sanitizeBrandOverride(branding, brandingOptions));
    return () => setBrand({});
  }, [setBrand, branding, brandingOptions]);
  const value = React.useMemo(
    () => ({ tenant: resolved, names: names ?? EMPTY_TENANCY_NAMES }),
    [resolved, names]
  );
  return /* @__PURE__ */ jsx(TenantReactContext.Provider, { value, children });
}
function useTenant() {
  const ctx = React.useContext(TenantReactContext);
  if (!ctx) throw new Error("useTenant must be used inside <TenantProvider>");
  return ctx.tenant;
}
function useOptionalTenant() {
  return React.useContext(TenantReactContext)?.tenant ?? null;
}
function useTenancyNames() {
  return React.useContext(TenantReactContext)?.names ?? EMPTY_TENANCY_NAMES;
}
function useCurrentOrg() {
  const ctx = React.useContext(TenantReactContext);
  if (!ctx) throw new Error("useCurrentOrg must be used inside <TenantProvider>");
  const t = ctx.tenant;
  const names = ctx.names;
  return {
    orgId: t.orgId,
    tenantId: t.tenantId,
    orgIdTenantId: t.orgIdTenantId,
    customerId: t.customerId,
    isGlobal: t.isGlobal,
    hasCustomerScope: t.hasCustomerScope,
    orgName: names.orgName ?? t.orgId,
    tenantName: names.tenantName ?? t.tenantId,
    customerName: names.customerName ?? t.customerId
  };
}
function useUserScope() {
  const t = useTenant();
  return { userId: t.userId, isUserScoped: t.isUserScoped };
}
var plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
function TenancyStructureView({
  structure,
  initialPersonId = null,
  view,
  onViewChange,
  renderTenantActions,
  className
}) {
  const [personId, setPersonId] = React.useState(initialPersonId);
  const [localView, setLocalView] = React.useState("map");
  const changeView = (next) => {
    setLocalView(next);
    onViewChange?.(next);
  };
  const [openTenant, setOpenTenant] = React.useState(null);
  const matrix = React.useMemo(() => accessMatrix(structure), [structure]);
  const reach = React.useMemo(() => {
    const row = matrix.rows.find((r) => r.person.userId === personId);
    return row ? new Set(Object.keys(row.roles)) : null;
  }, [matrix, personId]);
  const tenantCount = matrix.columns.length;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), "data-testid": "tenancy-structure", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: structure.customer.name }),
        " \xB7 ",
        plural(structure.orgs.length, "organization", "organizations"),
        " \xB7 ",
        plural(tenantCount, "tenant", "tenants"),
        " \xB7",
        " ",
        plural(structure.people.length, "person", "people")
      ] }),
      /* @__PURE__ */ jsx(MermaidCopy, { structure })
    ] }),
    structure.hiddenOrgs > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", role: "note", children: [
      plural(structure.hiddenOrgs, "more organization", "more organizations"),
      " of this customer \u2014 you are not a member, so ",
      structure.hiddenOrgs === 1 ? "it is" : "they are",
      " not shown."
    ] }) : null,
    /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "People can have roles in several tenants. Tenant access does not expose another person\u2019s private copies." }),
    /* @__PURE__ */ jsxs(Tabs, { value: view ?? localView, onValueChange: (next) => changeView(next), children: [
      /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: "map", children: "Map" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "access", children: "Access" })
      ] }),
      /* @__PURE__ */ jsxs(TabsContent, { value: "map", className: "space-y-3", children: [
        /* @__PURE__ */ jsxs("label", { className: "flex max-w-lg flex-wrap items-center gap-2 text-sm", children: [
          /* @__PURE__ */ jsx("span", { className: "shrink-0 text-muted-foreground", children: "Show access for" }),
          /* @__PURE__ */ jsxs(
            NativeSelect,
            {
              density: "sm",
              "aria-label": "Show where this person holds a role",
              value: personId ?? "",
              onChange: (e) => setPersonId(e.target.value || null),
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Everyone (all tenants)" }),
                structure.people.map((p) => /* @__PURE__ */ jsxs("option", { value: p.userId, children: [
                  personLabel(p),
                  p.pending ? " (invited)" : ""
                ] }, p.userId))
              ]
            }
          )
        ] }),
        structure.orgs.map((org) => /* @__PURE__ */ jsxs("section", { className: "rounded-lg border p-3", "aria-label": `Organization ${org.name}`, children: [
          /* @__PURE__ */ jsxs("h3", { className: "mb-2 flex items-center gap-2 text-sm font-semibold", children: [
            org.name,
            org.isRoot ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "root org" }) : null
          ] }),
          /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3", children: org.tenants.map((tenant) => {
            const people = peopleIn(structure, tenant.id);
            const lit = reach ? reach.has(tenant.id) : null;
            const roles = reach ? matrix.rows.find((r) => r.person.userId === personId)?.roles[tenant.id] : void 0;
            return /* @__PURE__ */ jsxs(
              "div",
              {
                "data-tenant-id": tenant.id,
                "data-reached": lit === null ? void 0 : String(lit),
                className: cn(
                  "rounded-md border bg-card p-2 text-sm transition-opacity",
                  lit === true && "ring-2 ring-primary",
                  lit === false && "border-dashed"
                ),
                children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
                    /* @__PURE__ */ jsx("span", { className: "font-medium", children: tenant.name }),
                    /* @__PURE__ */ jsxs("span", { className: "flex flex-wrap items-center gap-1", children: [
                      tenant.isRoot ? /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "root" }) : null,
                      roles?.map((r) => /* @__PURE__ */ jsx(Badge, { variant: "info", children: r }, r)),
                      lit === false ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No role for this person" }) : null
                    ] })
                  ] }),
                  /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      className: "mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline",
                      "aria-expanded": openTenant === tenant.id,
                      "aria-label": `Show people in ${org.name} / ${tenant.name}`,
                      onClick: () => setOpenTenant(openTenant === tenant.id ? null : tenant.id),
                      children: [
                        plural(people.length, "person", "people"),
                        " with a role here"
                      ]
                    }
                  ),
                  openTenant === tenant.id ? /* @__PURE__ */ jsx("ul", { className: "mt-1 space-y-0.5 text-xs", children: people.map(({ person, roles: held }) => /* @__PURE__ */ jsxs("li", { className: "flex justify-between gap-2", children: [
                    /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
                      /* @__PURE__ */ jsx(
                        "button",
                        {
                          type: "button",
                          className: "block max-w-full truncate text-left underline-offset-2 hover:underline",
                          "aria-label": `Show access for ${personLabel(person)}`,
                          onClick: () => setPersonId(person.userId),
                          children: personLabel(person)
                        }
                      ),
                      person.email && person.name ? /* @__PURE__ */ jsx("span", { className: "block truncate text-muted-foreground", children: person.email }) : null
                    ] }),
                    /* @__PURE__ */ jsx("span", { className: "max-w-[45%] truncate text-right text-muted-foreground", title: held.join(", "), children: held.join(", ") })
                  ] }, person.userId)) }) : null,
                  /* @__PURE__ */ jsx("div", { className: "mt-2 flex flex-wrap gap-1", children: tenant.teamsVisible ? tenant.teams.length ? tenant.teams.map((team) => /* @__PURE__ */ jsxs(Badge, { variant: "outline", title: team.isDefault ? "The tenant's default team" : void 0, children: [
                    team.name,
                    team.isDefault ? " \u2605" : "",
                    " \xB7 ",
                    team.members.length
                  ] }, team.id)) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No teams" }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Teams not shown: you hold no role here" }) }),
                  renderTenantActions ? /* @__PURE__ */ jsx("div", { className: "mt-2", children: renderTenantActions(org, tenant) }) : null
                ]
              },
              tenant.id
            );
          }) })
        ] }, org.id))
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "access", children: /* @__PURE__ */ jsx(AccessTable, { structure, onSelectPerson: (id) => {
        setPersonId(id);
        changeView("map");
      } }) })
    ] })
  ] });
}
function AccessTable({ structure, onSelectPerson }) {
  const [query, setQuery] = React.useState("");
  const matrix = React.useMemo(() => accessMatrix(structure), [structure]);
  const needle = query.trim().toLowerCase();
  const rows = needle ? matrix.rows.filter((r) => `${r.person.name ?? ""} ${r.person.email ?? ""}`.toLowerCase().includes(needle)) : matrix.rows;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        type: "search",
        placeholder: "Filter people",
        "aria-label": "Filter people",
        className: "max-w-xs",
        value: query,
        onChange: (e) => setQuery(e.target.value)
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "overflow-x-auto rounded-md border", children: /* @__PURE__ */ jsxs(Table, { children: [
      /* @__PURE__ */ jsxs(TableHeader, { children: [
        /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableHead, { rowSpan: 2, className: "sticky left-0 bg-background", children: "Person" }),
          structure.orgs.map((org) => /* @__PURE__ */ jsx(TableHead, { colSpan: org.tenants.length, scope: "colgroup", className: "border-l text-center", children: org.name }, org.id))
        ] }),
        /* @__PURE__ */ jsx(TableRow, { children: matrix.columns.map((c) => /* @__PURE__ */ jsx(TableHead, { scope: "col", className: "border-l", children: c.tenantName }, c.tenantId)) })
      ] }),
      /* @__PURE__ */ jsxs(TableBody, { children: [
        rows.map((row) => /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsxs(TableCell, { className: "sticky left-0 bg-background", children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "text-left font-medium underline-offset-2 hover:underline",
                "aria-label": `Show ${personLabel(row.person)} on map`,
                onClick: () => onSelectPerson(row.person.userId),
                children: personLabel(row.person)
              }
            ),
            row.person.email && row.person.name ? /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: row.person.email }) : null,
            row.person.pending ? /* @__PURE__ */ jsx(Badge, { variant: "warning", className: "mt-1", children: "invited" }) : null
          ] }),
          matrix.columns.map((c) => {
            const roles = row.roles[c.tenantId] ?? [];
            const teams = row.teams[c.tenantId] ?? [];
            return /* @__PURE__ */ jsxs(TableCell, { className: "border-l align-top", "data-cell": `${row.person.userId}:${c.tenantId}`, children: [
              roles.length ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1", children: roles.map((r) => /* @__PURE__ */ jsx(Badge, { variant: "info", children: r }, r)) }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No role" }),
              teams.length ? /* @__PURE__ */ jsx("div", { className: "mt-1 text-xs text-muted-foreground", children: teams.join(", ") }) : null
            ] }, c.tenantId);
          })
        ] }, row.person.userId)),
        rows.length === 0 ? /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: matrix.columns.length + 1, className: "py-6 text-center text-muted-foreground", children: needle ? "No people match your search." : "No people are listed." }) }) : null
      ] })
    ] }) })
  ] });
}
function MermaidCopy({ structure }) {
  const { copy, copied, failed } = useCopyToClipboard();
  const [withPeople, setWithPeople] = React.useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs", children: [
    /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1 text-muted-foreground", children: [
      /* @__PURE__ */ jsx("input", { type: "checkbox", checked: withPeople, onChange: (e) => setWithPeople(e.target.checked) }),
      "Include people in export"
    ] }),
    /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: () => void copy(toMermaid(structure, { people: withPeople })), children: copied ? "Copied" : failed ? "Copy failed" : "Copy as Mermaid" })
  ] });
}
function SettlementEvidenceView({ evidence, appUrl = "", renderLink }) {
  return /* @__PURE__ */ jsxs("section", { "aria-label": "Recorded native settlement", className: "my-4 space-y-3 rounded-lg border bg-card p-4", children: [
    /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: "L3 \u2192 L2 \u2192 L1 \xB7 linked settlement evidence" }),
    evidence.tracerId && /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-primary underline", children: renderLink ? renderLink(`/tracers/${encodeURIComponent(evidence.tracerId)}?scope=${evidence.scope}`, "Open funds tracer") : /* @__PURE__ */ jsx("a", { href: `${appUrl.replace(/\/$/, "")}/tracers/${encodeURIComponent(evidence.tracerId)}?scope=${evidence.scope}`, children: "Open funds tracer" }) }),
    /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
      "Recorded snapshot \xB7 ",
      new Date(evidence.observedAt).toISOString().replace("T", " ").replace(/\.\d{3}Z$/, " UTC"),
      ". ",
      evidence.note,
      " Dashboard filters below apply to the queried panels, not this snapshot."
    ] }),
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-3 lg:grid-cols-3", children: evidence.journeys.map((j) => /* @__PURE__ */ jsxs("article", { className: "min-w-0 rounded-lg border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsx("h4", { className: "font-medium", children: j.label }),
        /* @__PURE__ */ jsx("span", { className: "rounded-full border px-2 py-0.5 text-xs", children: settlementStage(j.legs) })
      ] }),
      j.amount && /* @__PURE__ */ jsxs("p", { className: "my-2 text-sm font-semibold", title: `${j.amount.raw} base units`, children: [
        formatUnits(j.amount.raw, j.amount.decimals),
        " ",
        j.amount.symbol,
        " \xB7 native withdrawal"
      ] }),
      /* @__PURE__ */ jsxs("details", { className: "my-2 text-xs", children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Settlement ID and commitment" }),
        /* @__PURE__ */ jsxs("dl", { className: "mt-2 space-y-1 break-all", children: [
          /* @__PURE__ */ jsx("dt", { children: "Settlement ID" }),
          /* @__PURE__ */ jsx("dd", { className: "select-all font-mono", children: j.id }),
          /* @__PURE__ */ jsx("dt", { children: "Commitment" }),
          /* @__PURE__ */ jsx("dd", { className: "select-all font-mono", children: j.commitment })
        ] })
      ] }),
      /* @__PURE__ */ jsx("ol", { "aria-label": `${j.label} settlement path`, className: "space-y-2", children: [3, 2, 1].map((layer, index) => {
        const leg = j.legs.find((l) => l.layer === layer);
        const href = leg ? `/transactions/${leg.txId}?scope=${evidence.scope}` : "";
        const label = leg ? shortenHash(leg.txHash) : "";
        return /* @__PURE__ */ jsxs("li", { children: [
          index > 0 && /* @__PURE__ */ jsx("p", { "aria-hidden": "true", className: "text-center text-muted-foreground", children: "\u2193 native Outbox settlement" }),
          /* @__PURE__ */ jsxs("div", { className: "rounded border bg-background p-2 text-sm", children: [
            /* @__PURE__ */ jsxs("p", { className: "font-medium", children: [
              "L",
              layer,
              " \xB7 ",
              leg ? layer === 3 ? "Submitted" : "Finalized" : "Not finalized in recording"
            ] }),
            leg && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs("div", { className: "my-1 flex items-center gap-2", title: leg.chainId, children: [
                /* @__PURE__ */ jsx(ChainAvatar, { chain: leg.chainId, size: "sm", showName: !leg.chainName }),
                leg.chainName && /* @__PURE__ */ jsx("span", { className: "text-xs", children: leg.chainName })
              ] }),
              /* @__PURE__ */ jsx("span", { className: "font-mono text-xs underline underline-offset-4", title: leg.txHash, children: renderLink ? renderLink(href, label) : /* @__PURE__ */ jsx("a", { href: `${appUrl.replace(/\/$/, "")}${href}`, children: label }) })
            ] })
          ] })
        ] }, layer);
      }) })
    ] }, j.id)) })
  ] });
}
function parseWorkspaceGuide(value) {
  if (!value || typeof value !== "object") return;
  const g = value;
  const text = (v) => typeof v === "string" && v.length > 0 && v.length <= 4e3;
  const card = (v) => v && text(v.label) && text(v.detail) && (v.audience === void 0 || text(v.audience));
  if (g.version !== 1 || ![g.title, g.summary, g.architecture_title, g.architecture_note, g.included_title, g.boundary].every(text) || !Array.isArray(g.layers) || g.layers.length > 8 || !g.layers.every(card) || !card(g.local) || !Array.isArray(g.personas) || g.personas.length > 20 || !g.personas.every(card) || !Array.isArray(g.outcomes) || g.outcomes.length > 12 || !g.outcomes.every(text) || !Array.isArray(g.links) || g.links.length > 8 || !g.links.every((l) => l && text(l.label) && typeof l.href === "string" && /^\/[a-z0-9/?=&_%.-]*$/i.test(l.href) && !l.href.startsWith("//"))) return;
  if (g.layer_relation !== void 0 && !(text(g.layer_relation) && g.layer_relation.length <= 120)) return;
  if (g.settlement_evidence !== void 0 && !SettlementEvidenceSchema.safeParse(g.settlement_evidence).success) return;
  return g;
}
function WorkspaceGuideView({ guide, appUrl = "", collapsed = false, renderLink, linksHint }) {
  const [open, setOpen] = React.useState(!collapsed);
  const id = React.useId();
  return /* @__PURE__ */ jsxs("section", { "aria-label": guide.title, className: "rounded-xl border bg-card text-card-foreground", children: [
    /* @__PURE__ */ jsxs("div", { className: "space-y-3 p-5", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
        /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold", children: guide.title }),
        /* @__PURE__ */ jsx("button", { className: "rounded-md border px-3 py-1.5 text-sm", "aria-expanded": open, "aria-controls": id, onClick: () => setOpen((v) => !v), children: open ? "Hide setup map" : "Show setup map" })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "max-w-4xl text-sm text-muted-foreground", children: guide.summary }),
      /* @__PURE__ */ jsx("nav", { "aria-label": "Demo walkthrough", className: "flex flex-wrap gap-3", children: guide.links.map((l) => /* @__PURE__ */ jsx("span", { className: "text-sm font-medium underline underline-offset-4", children: renderLink ? renderLink(l.href, l.label) : /* @__PURE__ */ jsx("a", { href: `${appUrl.replace(/\/$/, "")}${l.href}`, children: l.label }) }, l.href)) }),
      linksHint && guide.links.length > 0 && /* @__PURE__ */ jsx("p", { className: "max-w-4xl text-xs text-muted-foreground", children: linksHint })
    ] }),
    open && /* @__PURE__ */ jsxs("div", { id, className: "space-y-6 border-t p-5", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: guide.architecture_title }),
        /* @__PURE__ */ jsx("p", { className: "mb-4 mt-1 max-w-4xl text-sm text-muted-foreground", children: guide.architecture_note }),
        /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 lg:grid-cols-3", children: [
          /* @__PURE__ */ jsx("ol", { "aria-label": guide.layer_relation ? "Ledgers" : "Settlement hierarchy", className: "space-y-2 lg:col-span-2", children: guide.layers.map((n, i) => /* @__PURE__ */ jsxs("li", { children: [
            i > 0 && /* @__PURE__ */ jsx("p", { className: "py-1 text-center text-xs text-muted-foreground", children: guide.layer_relation ?? "\u2191 settles to its parent" }),
            /* @__PURE__ */ jsxs("div", { className: "rounded-lg border-l-4 border-l-primary bg-muted/50 p-4", children: [
              /* @__PURE__ */ jsx("h4", { className: "font-medium", children: n.label }),
              /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm", children: n.detail }),
              /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-muted-foreground", children: n.audience })
            ] })
          ] }, n.label)) }),
          /* @__PURE__ */ jsxs("aside", { className: "self-start rounded-lg border border-dashed p-4", children: [
            /* @__PURE__ */ jsx("h4", { className: "font-medium", children: guide.local.label }),
            /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm", children: guide.local.detail }),
            /* @__PURE__ */ jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: guide.local.audience }),
            /* @__PURE__ */ jsx("p", { className: "mt-4 border-t pt-3 text-xs", children: "Captured evidence \u2192 scoped Authe workspace" })
          ] })
        ] })
      ] }),
      guide.settlement_evidence && /* @__PURE__ */ jsx(SettlementEvidenceView, { evidence: guide.settlement_evidence, appUrl, renderLink }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: guide.included_title }),
        /* @__PURE__ */ jsx("ol", { className: "mt-3 list-decimal space-y-2 pl-5 text-sm", children: guide.outcomes.map((t) => /* @__PURE__ */ jsx("li", { children: t }, t)) })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: "Who uses each workspace" }),
        /* @__PURE__ */ jsx("p", { className: "mb-3 mt-1 text-sm text-muted-foreground", children: guide.boundary }),
        /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-3 md:grid-cols-2", children: guide.personas.map((p) => /* @__PURE__ */ jsxs("article", { className: "rounded-lg border p-3", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-medium", children: p.label }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-muted-foreground", children: p.detail })
        ] }, p.label)) })
      ] })
    ] })
  ] });
}
function PaymentEvidenceView({ evidence, renderLink }) {
  return /* @__PURE__ */ jsxs("section", { "aria-label": "Recorded enterprise payments", className: "my-4 space-y-4 rounded-lg border bg-card p-4", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: "Instruction \u2192 source ledger \u2192 destination ledger \u2192 signed cash evidence" }),
      /* @__PURE__ */ jsx(CopyButton, { value: [...new Set(evidence.journeys.flatMap((j) => j.legs.map((l) => l.txHash)))].join("\n"), label: "Copy all transaction hashes" })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
      "Recorded snapshot \xB7 ",
      new Date(evidence.observedAt).toISOString().replace("T", " "),
      ". ",
      evidence.note,
      " Filters below apply to queried panels, not this recording."
    ] }),
    /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-3 gap-3", "aria-label": "Recorded payment outcomes", children: [
      ["completed", "Confirmed payments"],
      ["open", "Needs follow-up"],
      ["cancelled", "Returned to sender"]
    ].map(([state, label]) => /* @__PURE__ */ jsxs("div", { className: "rounded border bg-background p-3", children: [
      /* @__PURE__ */ jsx("dt", { className: "text-xs text-muted-foreground", children: label }),
      /* @__PURE__ */ jsx("dd", { className: "mt-1 text-2xl font-semibold", children: evidence.journeys.filter((j) => j.state === state || state === "open" && j.state === "partial").length })
    ] }, state)) }),
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-4 xl:grid-cols-2", children: evidence.journeys.map((j) => {
      const units = (raw) => `${formatUnits(raw, j.decimals)} ${j.symbol}`;
      return /* @__PURE__ */ jsxs("article", { className: "min-w-0 rounded-lg border bg-background p-4", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap justify-between gap-2", children: [
          /* @__PURE__ */ jsx("h4", { className: "font-semibold", children: j.label }),
          /* @__PURE__ */ jsx("span", { className: "rounded-full border px-2 py-0.5 text-xs", children: j.state === "completed" ? "Completed with signed evidence" : j.state === "cancelled" ? "Returned to sender" : "Needs follow-up" })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "my-2 flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "break-all font-mono text-xs", children: j.id }),
          /* @__PURE__ */ jsx(CopyButton, { value: j.id, label: "Copy payment reference" })
        ] }),
        /* @__PURE__ */ jsxs("dl", { className: "mb-3 grid grid-cols-2 gap-x-3 text-sm", children: [
          /* @__PURE__ */ jsx("dt", { children: "Instructed" }),
          /* @__PURE__ */ jsx("dd", { children: units(j.requestedRaw) }),
          /* @__PURE__ */ jsx("dt", { children: "Paid out" }),
          /* @__PURE__ */ jsx("dd", { children: units(j.deliveredRaw) }),
          /* @__PURE__ */ jsx("dt", { children: "Returned by beneficiary" }),
          /* @__PURE__ */ jsx("dd", { children: units(j.returnedRaw) }),
          /* @__PURE__ */ jsx("dt", { children: "Still to deliver" }),
          /* @__PURE__ */ jsx("dd", { children: j.state === "cancelled" ? "Cancelled \xB7 no payment due" : units(j.outstandingRaw) })
        ] }),
        /* @__PURE__ */ jsx("ol", { className: "space-y-2", "aria-label": `${j.label} payment stages`, children: j.legs.map((l) => /* @__PURE__ */ jsxs("li", { className: "rounded border p-2 text-sm", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap justify-between gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "font-medium", children: l.stage }),
            l.late && /* @__PURE__ */ jsx("span", { className: "font-medium text-warning", children: "Arrived after deadline" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "my-1 flex items-center gap-2", title: l.chainId, children: [
            /* @__PURE__ */ jsx(ChainAvatar, { chain: l.chainId, size: "sm", showName: false }),
            /* @__PURE__ */ jsx("span", { children: l.chainName }),
            l.amountRaw !== void 0 && /* @__PURE__ */ jsx("span", { className: "ml-auto", children: units(l.amountRaw) })
          ] }),
          /* @__PURE__ */ jsx("span", { className: "font-mono text-xs underline", title: l.txHash, children: renderLink(`/transactions/${l.txId}?scope=${evidence.scope}`, shortenHash(l.txHash)) }),
          /* @__PURE__ */ jsx(CopyButton, { value: l.txHash, label: "Copy transaction hash" })
        ] }, `${l.stage}-${l.txId}`)) }),
        !j.legs.some((l) => l.stage === "Destination payout") && /* @__PURE__ */ jsx("p", { className: "my-2 text-sm", children: "Destination leg not observed." }),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 rounded border p-2 text-sm", children: [
          /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer font-medium", children: [
            "Signed external evidence \xB7 ",
            j.evidence.verified ? "matched to ledger claim" : j.evidence.status
          ] }),
          /* @__PURE__ */ jsxs("p", { className: "my-2", children: [
            j.evidence.source,
            ". ",
            j.evidence.verified ? "Signature, ledger binding, exact amount and observation reservation verified." : "This payment has no verified cash confirmation.",
            " Legal finality is not assessed."
          ] }),
          /* @__PURE__ */ jsxs("dl", { className: "grid grid-cols-2 gap-x-3", children: [
            /* @__PURE__ */ jsx("dt", { children: "Bank statement" }),
            /* @__PURE__ */ jsx("dd", { children: j.evidence.observedRaw === null ? "Unavailable" : units(j.evidence.observedRaw) }),
            /* @__PURE__ */ jsx("dt", { children: "Difference from claim" }),
            /* @__PURE__ */ jsx("dd", { children: j.evidence.residualRaw === null ? "Unknown" : units(j.evidence.residualRaw) })
          ] }),
          j.evidence.digest && /* @__PURE__ */ jsxs("p", { className: "mt-2 break-all font-mono text-xs", children: [
            "Statement SHA-256: ",
            j.evidence.digest
          ] })
        ] })
      ] }, j.id);
    }) })
  ] });
}

export { PaymentEvidenceView, SettlementEvidenceView, TenancyStructureView, TenantProvider, WorkspaceGuideView, parseWorkspaceGuide, useCurrentOrg, useOptionalTenant, useTenancyNames, useTenant, useUserScope };
//# sourceMappingURL=chunk-KIKTMVX5.js.map
//# sourceMappingURL=chunk-KIKTMVX5.js.map